import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import AddLinkRounded from '@mui/icons-material/AddLinkRounded';
import LanguageRounded from '@mui/icons-material/LanguageRounded';
import {
  EmptyState, ListTable, Panel, StatusChip, ToolbarButton, type ListColumn,
} from '../../../ui-kit';
import { fetchWaIntegrations, type WaIntegration } from '../../../services/websiteAssistantService';
import { useLive } from '../rocco/live';
import { AgencyZoomMark } from '../missed-call/logos';
import { PolicySystemLogo } from '../policy-servicing/logos';

const LOGOS: Record<WaIntegration['id'], React.ReactNode> = {
  website: <LanguageRounded sx={(t) => ({ fontSize: 22, color: t.tokens.brand.primary })} />,
  agencyzoom: <AgencyZoomMark />,
  policy_system: <PolicySystemLogo />,
};

const ALSO_WORKS_WITH: Record<WaIntegration['id'], string> = {
  website: 'Any website, with one line of code',
  agencyzoom: 'HubSpot or Salesforce, once connected',
  policy_system: 'Boston Software, AMS360, Applied Epic or HawkSoft, once connected',
};

/** What the Website Assistant is joined to: the website, AgencyZoom, and the policy system. */
export default function IntegrationsPage() {
  const navigate = useNavigate();
  const { data, error, loading } = useLive(fetchWaIntegrations, 30_000);
  if (error && !data) return <EmptyState message={error} />;

  const columns: ListColumn<WaIntegration>[] = [
    {
      key: 'name', header: 'System', grow: 1.3, minWidth: 220,
      render: (row) => (
        <Stack direction="row" alignItems="center" spacing={1.25}>
          <Box sx={{ display: 'flex', flexShrink: 0 }}>{LOGOS[row.id]}</Box>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>{row.name}</Typography>
        </Stack>
      ),
    },
    {
      key: 'role', header: 'Does', width: 170,
      render: (row) => <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.secondary })}>{row.role}</Typography>,
    },
    {
      key: 'state', header: 'State', width: 200,
      render: (row) => (row.id === 'agencyzoom' && !row.connected ? (
        <ToolbarButton variant="outlined" icon={<AddLinkRounded />} onClick={() => navigate('/connectors')}>
          Connect AgencyZoom
        </ToolbarButton>
      ) : <StatusChip tone={row.connected ? 'good' : 'neutral'} label={row.connected ? 'Connected' : 'Not connected'} />),
    },
    {
      key: 'detail', header: 'Today', grow: 2, minWidth: 240,
      render: (row) => (
        <Stack sx={{ minWidth: 0 }}>
          <Typography variant="body2" sx={(t) => ({ color: t.tokens.text.primary })}>
            {row.detail}
            {row.path ? (
              <>
                {' · '}
                <Box component="a" href={row.path} target="_blank" rel="noopener"
                  sx={(t) => ({ color: t.tokens.brand.primary })}>Open it</Box>
              </>
            ) : null}
          </Typography>
          <Typography variant="caption" sx={(t) => ({ color: t.tokens.text.tertiary })}>
            Also works with {ALSO_WORKS_WITH[row.id]}
          </Typography>
        </Stack>
      ),
    },
  ];

  return (
    <Panel title="Connected systems" subtitle="Visitors in, leads and call-backs out, clients' policies read · as they are now">
      <ListTable columns={columns} rows={data?.rows ?? []} getRowKey={(row) => row.id} loading={loading}
        emptyMessage="Nothing connected." />
    </Panel>
  );
}
